import throttle from 'lodash/throttle';
import * as React from 'react';

const SWIPE_THRESHOLD = 5;

export interface IAbstractNavigationProps {
  currentSlide: number;
  offsetStep?: number;
  slideOffset?: number;

  onChangeSlide(index: number): void;
}

export interface ICoordinates {
  currentX: number;
}

export interface IAbstractNavigationState {
  offsetLeft?: number;
  swipeStart?: boolean;
  swiped?: boolean;
  coordinates?: ICoordinates;
  imagesLoaded?: boolean;
}

interface IAbstractNavigationClass {
  containerElement: HTMLElement | null;
  navigationElement: HTMLElement | null;
  containerRect: ClientRect;
  navigationRect: ClientRect;
  navigationElements: Map<number, HTMLElement>;
  onNavigationItemClick(e: React.MouseEvent<HTMLElement>, index: number): void;
  onSwipeStart(e: React.MouseEvent<HTMLElement> | React.TouchEvent<HTMLElement>): void;
  onSwipeMove(e: React.MouseEvent<HTMLElement> | React.TouchEvent<HTMLElement>): void;
}

export abstract class AbstractNavigation<P extends IAbstractNavigationProps, S extends IAbstractNavigationState>
  extends React.Component<P, S>
  implements IAbstractNavigationClass
{
  public abstract containerElement: HTMLElement | null;
  public abstract navigationElement: HTMLElement | null;
  public containerRect: ClientRect;
  public navigationRect: ClientRect;
  public abstract navigationElements: Map<number, HTMLElement>;
  public imagesLoaded = 0;
  public resizeThrottleFn: Function;
  public constructor(props: P) {
    super(props);
    this.state = {
      imagesLoaded: false,
      offsetLeft: 0,
    } as S;

    this.onResizeHandler = this.onResizeHandler.bind(this);
  }

  public componentDidMount() {
    this.updateSizes();
    window.addEventListener('resize', this.onResizeHandler);
  }

  public UNSAFE_componentWillReceiveProps(nextProps: P) {
    if (this.props.currentSlide !== nextProps.currentSlide) {
      const offsetLeft = this.calculateCurrentOffset(this.navigationElements.get(nextProps.currentSlide));
      if (typeof offsetLeft === 'number') {
        this.setState({
          offsetLeft,
          swipeStart: false,
        });
      }
    }
  }

  public componentWillUnmount() {
    window.removeEventListener('resize', this.onResizeHandler);
  }

  public imageOnLoad() {
    this.imagesLoaded++;
    if (this.imagesLoaded < this.navigationElements.size) {
      return;
    }
    this.setState({ imagesLoaded: true }, this.updateSizes);
  }

  public onNavigationItemClick = (e: React.MouseEvent<HTMLElement> | React.TouchEvent<HTMLElement>, index: number) => {
    if (this.state.swiped) {
      e.preventDefault();

      return;
    }
    const offsetLeft = this.calculateCurrentOffset(this.navigationElements.get(index));

    if (typeof offsetLeft === 'number') {
      this.setState(
        {
          offsetLeft,
          swipeStart: false,
        },
        () => this.props.onChangeSlide(index),
      );

      return;
    }
    this.setState({ swipeStart: false });
    this.props.onChangeSlide(index);
  };

  public onSwipeStart = (e: React.MouseEvent<HTMLElement> | React.TouchEvent<HTMLElement>) => {
    if (this.state.swiped) {
      return;
    }

    const posX = instanceOfHelper<React.SyntheticEvent<HTMLElement>, React.TouchEvent<HTMLElement>>(e, 'touches')
      ? e.touches[0].pageX
      : e.clientX;

    this.setState({
      coordinates: {
        currentX: posX,
      },
      swipeStart: true,
    });
  };

  public onSwipeMove = (e: React.MouseEvent<HTMLElement> | React.TouchEvent<HTMLElement>) => {
    const { swipeStart, coordinates } = this.state;
    if (!swipeStart || !coordinates) {
      return;
    }

    const offset = this.navigationRect.width - this.containerRect.width;

    if (offset === 0) {
      return;
    }

    let offsetLeft = Number(this.state.offsetLeft);

    const posX = instanceOfHelper<React.SyntheticEvent<HTMLElement>, React.TouchEvent<HTMLElement>>(e, 'touches')
      ? e.touches[0].pageX
      : e.clientX;

    const mouseWidth = posX - coordinates.currentX;

    if (Math.abs(mouseWidth) < SWIPE_THRESHOLD) {
      return;
    }

    if (mouseWidth < 0) {
      offsetLeft += mouseWidth * -1;
      this.setState({
        coordinates: {
          currentX: posX,
        },
        offsetLeft: offsetLeft > offset ? offsetLeft - (offsetLeft - offset) : offsetLeft,
        swiped: true,
      });

      return;
    }

    offsetLeft -= mouseWidth;
    this.setState({
      coordinates: {
        currentX: posX,
      },
      offsetLeft: offsetLeft <= 0 ? 0 : offsetLeft,
      swiped: true,
    });
  };

  public updateSizes = () => {
    this.recalculateRect();
    const offsetLeft = this.calculateCurrentOffset(this.navigationElements.get(this.props.currentSlide));

    if (typeof offsetLeft === 'number') {
      this.setState({
        offsetLeft,
        swipeStart: false,
      });
    }
  };

  public calculateCurrentOffset = (element?: HTMLElement): number | void => {
    if (!element) {
      return undefined;
    }
    const itemRect = element.getBoundingClientRect();
    const itemOffset = itemRect.left + itemRect.width;

    if (itemOffset > this.containerRect.width + this.containerRect.left) {
      return Number(this.state.offsetLeft) + (itemOffset - (this.containerRect.width + this.containerRect.left));
    } else if (itemRect.left < this.containerRect.left) {
      return Number(this.state.offsetLeft) - (this.containerRect.left - itemRect.left);
    }
  };

  public recalculateRect = () => {
    if (!this.containerElement || !this.navigationElement) {
      return;
    }
    this.containerRect = this.containerElement.getBoundingClientRect();
    const navigationWidth = Array.from(this.navigationElements.values()).reduce(
      (width, element) => width + element.getBoundingClientRect().width + (Number(this.props.slideOffset) || 0),
      1,
    );
    this.navigationElement.style.width = `${Math.ceil(navigationWidth)}px`;
    this.navigationRect = this.navigationElement.getBoundingClientRect();
  };

  public onResizeHandler = () => {
    if (!this.resizeThrottleFn) {
      this.resizeThrottleFn = throttle(() => this.setState({ offsetLeft: 0 }, this.updateSizes), 400);
    }

    this.resizeThrottleFn();
  };
}

export function instanceOfHelper<T, P extends {}>(Obj: T | P, ...propterties: string[]): Obj is P {
  return propterties.every(prop => prop in (Obj as P));
}
